// src/webideal-jobs/pages/Companies.jsx

import React, { useState } from 'react';
import { backendApi } from '../../api/backendApi';
import { useQuery } from '@tanstack/react-query';

import {
  Search,
  Building2,
  MapPin,
  Star,
  TrendingUp,
  Filter,
  Briefcase
} from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export default function Companies() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('all');

  // ------------------------------------------
  // LOAD JOBS (backendApi)
  // ------------------------------------------
  const { data: jobs = [] } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => backendApi.entities.Job.list(),
  });

  // ------------------------------------------
  // SAFELY FORMAT COMPANIES FROM JOB LIST
  // ------------------------------------------
  const companies = React.useMemo(() => {
    const map = new Map();

    jobs.forEach((job) => {
      const companyName = job?.company?.trim() || "Unknown Company";
      const location = job?.location || "Not Specified";

      if (!map.has(companyName)) {
        map.set(companyName, {
          name: companyName,
          logo: job?.company_logo ?? null,
          locations: new Set([location]),
          jobCount: 1,
          rating: (3.5 + Math.random() * 1.5).toFixed(1),
          reviews: Math.floor(100 + Math.random() * 900),
        });
      } else {
        const c = map.get(companyName);
        c.locations.add(location);
        c.jobCount++;
      }
    });

    return Array.from(map.values()).map((c) => ({
      ...c,
      locations: Array.from(c.locations),
    }));
  }, [jobs]);

  // ------------------------------------------
  // SAFE SEARCH FILTER
  // ------------------------------------------
  const filteredCompanies = companies.filter((company) => {
    const safeName = company?.name?.toLowerCase?.() || "";
    return safeName.includes(searchQuery.toLowerCase());
  });

  const industries = ['all', 'IT Services', 'E-commerce', 'Finance', 'Healthcare', 'Consulting'];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* HERO SECTION */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-12">
        <div className="w-full px-4">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-3">Explore Top Companies</h1>
            <p className="text-blue-100 text-lg">
              Discover companies, their culture, and open positions
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search companies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-14 pl-12 pr-4 text-lg rounded-xl border-0 shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <main className="w-full px-4 py-8">

        {/* Filters */}
        <div className="flex items-center gap-3 mb-6 overflow-x-auto pb-2">
          <Button variant="outline" size="sm" className="gap-2 flex-shrink-0">
            <Filter className="w-4 h-4" />
            All Filters
          </Button>

          {industries.map((industry) => (
            <Button
              key={industry}
              variant={selectedIndustry === industry ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedIndustry(industry)}
              className="flex-shrink-0"
            >
              {industry}
            </Button>
          ))}
        </div>

        {/* Stats */}
        <h2 className="text-xl font-semibold mb-1">
          {filteredCompanies.length} Companies Found
        </h2>
        <p className="text-gray-500 text-sm mb-6">Browse and explore top employers</p>

        {/* Company Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCompanies.map((company, index) => (
            <Card
              key={index}
              className="p-6 hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              {/* Logo + Rating */}
              <div className="flex items-start justify-between mb-4">
                <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center border">
                  {company.logo ? (
                    <img
                      src={company.logo}
                      alt={company.name}
                      className="w-12 h-12 object-contain"
                    />
                  ) : (
                    <Building2 className="w-8 h-8 text-gray-400" />
                  )}
                </div>

                <Badge className="bg-green-100 text-green-700 border border-green-200">
                  <Star className="w-3 h-3 mr-1 fill-green-700" />
                  {company.rating}
                </Badge>
              </div>

              {/* Name */}
              <h3 className="font-bold text-gray-900 text-lg mb-2">
                {company.name}
              </h3>

              {/* Location */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{company.locations.slice(0, 2).join(', ')}</span>
                </div>
                <p className="text-xs text-gray-500">
                  {company.reviews.toLocaleString()} reviews
                </p>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <span className="text-sm font-medium text-blue-600">
                  {company.jobCount} open {company.jobCount === 1 ? 'position' : 'positions'}
                </span>
                <Button size="sm" variant="ghost" className="text-blue-600 hover:bg-blue-50">
                  View jobs →
                </Button>
              </div>
            </Card>
          ))}
        </div>

        {/* Empty State */}
        {filteredCompanies.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-100 rounded-full mx-auto flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">No companies found</h3>
            <p className="text-gray-500">Try adjusting your search</p>
          </div>
        )}

        {/* Featured Section */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold mb-6">Why Join These Companies?</h2>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-6">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold mb-2">Career Growth</h3>
              <p className="text-sm text-gray-600">
                Grow quickly with leading employers.
              </p>
            </Card>

            <Card className="p-6">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">Great Culture</h3>
              <p className="text-sm text-gray-600">
                Work in supportive, employee-first companies.
              </p>
            </Card>

            <Card className="p-6">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Briefcase className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-semibold mb-2">Competitive Benefits</h3>
              <p className="text-sm text-gray-600">
                Enjoy excellent pay and full benefits.
              </p>
            </Card>
          </div>
        </section>
      </main>
    </div>
  );
}
